import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { JamInventraLogoFull } from '../components/JamInventraLogo';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('expired') === '1') {
      toast.info('Your session has expired. Please log in again.');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/app/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 lg:px-16 bg-white">
        <div className="max-w-md w-full mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-[#0A0A0A]/60 hover:text-[#0A0A0A] mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          
          <div className="mb-8">
            <JamInventraLogoFull height={50} />
            <p className="text-[#0A0A0A]/30 text-xs mt-2 tracking-wide">A product of LOM Solutions Jamaica Limited</p>
          </div>
          
          <h1 className="text-heading-black text-4xl text-[#0A0A0A] tracking-tight mb-2">
            WELCOME BACK
          </h1>
          <p className="text-[#0A0A0A]/60 mb-8">
            Sign in to your account to continue
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-[#0A0A0A]">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="h-12 border-[#E5E5EA] focus:border-[#007AFF] focus:ring-[#007AFF]"
                data-testid="login-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-[#0A0A0A]">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12 border-[#E5E5EA] focus:border-[#007AFF] focus:ring-[#007AFF]"
                data-testid="login-password-input"
              />
            </div>

            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-sm text-[#007AFF] hover:underline">
                Forgot password?
              </Link>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 bg-[#007AFF] hover:bg-[#0066D6] text-white btn-hover"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <p className="mt-8 text-center text-[#0A0A0A]/60">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#007AFF] hover:underline font-medium">
              Start free trial
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1614529168796-cb235d6a2557?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHw0fHxqYW1haWNhJTIwY2FyaWJiZWFuJTIwbWFya2V0JTIwY29sb3JmdWwlMjBjbG90aGVzJTIwc2hvcCUyMHRyb3BpY2FsfGVufDB8fHx8MTc3MzcyMjYyM3ww&ixlib=rb-4.1.0&q=85')" }}
        />
        <div className="absolute inset-0 bg-[#0A0A0A]/40" />
        <div className="absolute inset-0 flex items-end p-12">
          <div>
            <p className="text-overline text-[#007AFF] mb-2">TRUSTED BY BUSINESSES</p>
            <h2 className="text-heading-black text-3xl text-white tracking-tight">
              MANAGE YOUR<br />INVENTORY SMARTER
            </h2>
          </div>
        </div>
      </div>
    </div>
  );
}
